import { Injectable } from '@angular/core';
import { TimeFrameUtilService } from '@backoffice-frontend/shared/bo/util-masterdata';
import type {
  Area,
  TimeSeriesAnalysisIdentifierDataLocalized,
  TimeSeriesIdentifierInput,
} from '@clean-code/models';
import {
  EnergySourceEnum,
  IndividualTimeFrame,
  SpreadCollection,
  SpreadTable,
  TimeFrame,
} from '@clean-code/models';
import { GraphqlService } from '@clean-code/shared/util-graphql';
import { Observable } from 'rxjs';

@Injectable({ providedIn: 'root' })
export class SpreadDataService {
  constructor(private graphqlService: GraphqlService) {}

  getAll$(
    symbols: TimeSeriesAnalysisIdentifierDataLocalized[],
    timeFrame: TimeFrame,
  ): Observable<SpreadCollection> {
    const query = `
    query($symbols: [TimeSeriesInput!]!, $timeFrame: TimeSeriesTimeFrame, $individualTimeFrame: IndividualTimeFrameInput){
      spreadGetAll (symbols: $symbols, timeFrame: $timeFrame, individualTimeFrame: $individualTimeFrame){
        spreads
        spreadsAvg
        avg
        timeSeries
        unit
      }
    }`;

    const variables = {
      symbols: symbols,
      timeFrame: null as string,
      individualTimeFrame: null as IndividualTimeFrame,
    };

    TimeFrameUtilService.setTimeFrameVariable(timeFrame, variables);

    return this.graphqlService.query<SpreadCollection>(query, variables);
  }

  getTable$(
    symbols: TimeSeriesAnalysisIdentifierDataLocalized[],
    timeFrame: TimeFrame,
  ): Observable<SpreadTable> {
    const query = `
    query($symbols: [TimeSeriesInput!], $timeFrame: TimeSeriesTimeFrame, $individualTimeFrame: IndividualTimeFrameInput){
      spreadGetTable (symbols: $symbols, timeFrame: $timeFrame, individualTimeFrame: $individualTimeFrame){
          current
          unit
          avg {
            spread1
            spread2
            values
            {
              key
              value
              {
                spread1Value
                spread2Value
                avg
              }
            }
          }
      }
    }`;

    const variables = {
      symbols,
      timeFrame: null as string,
      individualTimeFrame: null as IndividualTimeFrame,
    };

    TimeFrameUtilService.setTimeFrameVariable(timeFrame, variables);

    return this.graphqlService.query<SpreadTable>(query, variables);
  }

  withDynamicInput$(
    symbols: TimeSeriesIdentifierInput[],
    timeFrame: TimeFrame,
  ): Observable<SpreadCollection> {
    const query = `
    query($symbols: [TimeSeriesInput!]!, $timeFrame: TimeSeriesTimeFrame, $individualTimeFrame: IndividualTimeFrameInput){
      spreadsWithDynamicInput (symbols: $symbols, timeFrame: $timeFrame, individualTimeFrame: $individualTimeFrame){
        spreads
        spreadsAvg
        avg
        timeSeries
        unit
      }
    }`;

    const variables = {
      symbols,
      timeFrame: null as string,
      individualTimeFrame: null as IndividualTimeFrame,
    };

    TimeFrameUtilService.setTimeFrameVariable(timeFrame, variables);

    return this.graphqlService.query<SpreadCollection>(query, variables);
  }

  //TODO: possibly better to move this to area.service?
  //      but area.service is using graphql.net at the moment
  public getAllMarketAreasByEnergySource$(
    energySource: EnergySourceEnum,
  ): Observable<Area[]> {
    const query = `
        query ($energySource: EnergySourceEnum!) {
          marketAreas (
            where: {
              and: [ 
                {energySource: {eq: $energySource } }, 
                {disabled: {eq: false}} 
              ] 
            }
          ) {
            id
            name
            longName
            country
            energySource
            disabled
            timeZoneId
            timeRangeFrom
            timeRangeTo
            }
        }
    `;

    const variables = { energySource: energySource };

    return this.graphqlService.query<Area[]>(query, variables);
  }
}
